import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import Loader from 'Components/Loader'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { commonMethods } from 'Utils/commonMethods'
import { InputText } from 'primereact/inputtext'

const EditForexData = ({ setLoading, forexDataList, setForexDataList, filteredForexDataList, setFilteredForexDataList, baseCurrencyList, targetCurrenyTypeList }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(location.search)
  const { utcToLocalDate, formatDateToISOWithoutTimezone } = commonMethods
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })

  const [selectedBaseCurrnecy, setSelectedBaseCurrnecy] = useState(null)
  const [selectedTargetCurrnecy, setSelectedTargetCurrnecy] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [editForexDataDetails, setEditForexDataDetails] = useState({})

  useEffect(() => {
    const forexId = searchParams.get('forexId')
    const selectedForex = forexDataList.find((ele) => ele.id === Number(forexId))
    const selectedCurrnecy = targetCurrenyTypeList.find((ele) => ele.symbolTicker === selectedForex?.symbolTicker)
    const selectedDateType = new Date(selectedForex?.dataDtm)

    setSelectedBaseCurrnecy(baseCurrencyList[0])
    setSelectedTargetCurrnecy(selectedCurrnecy)
    setSelectedDate(selectedDateType)
    setEditForexDataDetails(selectedForex)
  }, [forexDataList])

  const handleBaseCurrencyDropdownChange = (e) => {
    const { baseCountryId, baseCountryIso3, baseCountryName, baseCurrencyCode, baseCurrencyName } = e.value

    setEditForexDataDetails({ ...editForexDataDetails, baseCountryId, baseCountryIso3, baseCountryName, baseCurrencyCode, baseCurrencyName })
    setSelectedBaseCurrnecy(e.value)
  }

  const handleTargetCurrencyDropdownChange = (e) => {
    const { symbolTicker, targetCountryId, targetCountryIso3, targetCountryName, targetCurrencyCode, targetCurrencyName } = e.value

    setEditForexDataDetails({ ...editForexDataDetails, symbolTicker, targetCountryId, targetCountryIso3, targetCountryName, targetCurrencyCode, targetCurrencyName })
    setSelectedTargetCurrnecy(e.value)
  }

  const handleSelectDate = (e) => {
    const isoTime = formatDateToISOWithoutTimezone(e.value)
    const isoString = `${isoTime.replace(' ', 'T')}Z`

    const formattedDate = utcToLocalDate(isoTime)

    setEditForexDataDetails({ ...editForexDataDetails, dataDtm: isoString, formattedDate })
    setSelectedDate(e.value)
  }

  const handleChangeInput = (e) => {
    const { name, value } = e.target

    const numberOnlyFields = ['currencyOpen', 'currencyHigh', 'currencyLow', 'currencyClose', 'currencyPreClose', 'currencyNetChg', 'currencyPctChg']
    const isCurrencyField = numberOnlyFields.includes(name)
    const regex = /^\d*\.?\d{0,4}$/

    if (!isCurrencyField || regex.test(value)) {
      setEditForexDataDetails((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleClickSaveButton = async () => {
    const { formattedDate, currencyOpen, currencyClose, currencyHigh, currencyLow, currencyNetChg, currencyPreClose, ...rest } = editForexDataDetails

    const postData = { ...rest, currencyOpen, currencyClose, currencyHigh, currencyLow, currencyNetChg, currencyPreClose }

    setLoading(true)
    try {
      const res = await apiAdapterCoffeeWeb.updateForexDayData(postData)

      if (res?.data?.isSuccess) {
        const modifiedData = forexDataList?.map((ele) => (ele.id === editForexDataDetails.id ? editForexDataDetails : ele))
        const modifiedFilteredData = filteredForexDataList?.map((ele) => (ele.id === editForexDataDetails.id ? editForexDataDetails : ele))

        setForexDataList(modifiedData)
        setFilteredForexDataList(modifiedFilteredData)

        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('FOREX_DATA_UPDATED_SUCCESSFULLY'),
          onHide: handleSuccess,
          onSuccess: handleSuccess
        })
      } else {
        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'error',
          message: t('SOMETHING_WENT_WRONG'),
          onHide: handleDismiss,
          onRetry: handleClickSaveButton
        })
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: error.response.data.message || t('SOMETHING_WENT_WRONG'),
        onHide: handleDismiss,
        onRetry: handleClickSaveButton
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSuccess = () => {
    setShowDialog({ ...showDialog, showModal: false })
    history.goBack()
  }

  const handleDismiss = () => setShowDialog({ ...showDialog, showModal: false })

  const isSaveButtonEnabled = selectedBaseCurrnecy && selectedTargetCurrnecy && selectedDate && editForexDataDetails?.currencyOpen && editForexDataDetails?.currencyHigh && editForexDataDetails?.currencyLow && editForexDataDetails?.currencyClose && editForexDataDetails?.currencyPreClose && editForexDataDetails?.currencyNetChg && editForexDataDetails?.currencyPctChg

  return (
    <div className="edit-forex-component-wrapper" data-testid="edit-forex-component-wrapper">
      <div className="edit-forex-form">
        <div className="p-float-label">
          <Calendar value={selectedDate} onChange={(e) => handleSelectDate(e)} dateFormat="dd M yy" data-testid="calender" />
          <label htmlFor="menuPath">
            <span>{t('SELECT_DATE')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <Dropdown value={selectedBaseCurrnecy} onChange={(e) => handleBaseCurrencyDropdownChange(e)} options={baseCurrencyList} optionLabel="currencyLabel" className="w-full md:w-14rem" data-testid="base-currency-dropdown" />
          <label htmlFor="menuPath">
            <span>{t('BASE_CURRENCY')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <Dropdown value={selectedTargetCurrnecy} onChange={(e) => handleTargetCurrencyDropdownChange(e)} options={targetCurrenyTypeList} optionLabel="currencyLabel" className="w-full md:w-14rem" data-testid="target-currency-dropdown" />
          <label htmlFor="menuPath">
            <span>{t('TARGET_CURRENCY')}</span> <span className="mandate">*</span>
          </label>
        </div>

        <div className="p-float-label">
          <InputText name="currencyOpen" onChange={(e) => handleChangeInput(e)} value={editForexDataDetails?.currencyOpen} placeholder={t('OPEN')} data-testid="currency-open-field" />
          <label htmlFor="menuPath">
            <span>{t('OPEN')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <InputText name="currencyHigh" onChange={(e) => handleChangeInput(e)} value={editForexDataDetails?.currencyHigh} placeholder={t('HIGH')} data-testid="currency-high-field" />
          <label htmlFor="menuPath">
            <span>{t('HIGH')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <InputText name="currencyLow" onChange={(e) => handleChangeInput(e)} value={editForexDataDetails?.currencyLow} placeholder={t('LOW')} data-testid="currency-low-field" />
          <label htmlFor="menuPath">
            <span>{t('LOW')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <InputText name="currencyClose" onChange={(e) => handleChangeInput(e)} value={editForexDataDetails?.currencyClose} placeholder={t('Close')} data-testid="currency-close-field" />
          <label htmlFor="menuPath">
            <span>{t('Close')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <InputText name="currencyPreClose" onChange={(e) => handleChangeInput(e)} value={editForexDataDetails?.currencyPreClose} placeholder={t('PRE_CLOSE')} data-testid="currency-preClose-field" />
          <label htmlFor="menuPath">
            <span>{t('PRE_CLOSE')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <InputText name="currencyNetChg" onChange={(e) => handleChangeInput(e)} value={editForexDataDetails?.currencyNetChg} placeholder={t('NET_CHANGE')} data-testid="currency-netChange-field" />
          <label htmlFor="menuPath">
            <span>{t('NET_CHANGE')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="p-float-label">
          <InputText name="currencyPctChg" onChange={(e) => handleChangeInput(e)} value={editForexDataDetails?.currencyPctChg} placeholder={t('PERCENTAGE_CHANGE')} data-testid="currency-PctChg-field" />
          <label htmlFor="menuPath">
            <span>{t('PERCENTAGE_CHANGE')}</span> <span className="mandate">*</span>
          </label>
        </div>
        <div className="edit-forex-form-footer">
          <ButtonComponent type={'submit'} onClick={handleClickSaveButton} disabled={!isSaveButtonEnabled} />
        </div>
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />
    </div>
  )
}

export default Loader(EditForexData)
